import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { ChevronLeft, ChevronRight, Gift, Loader2, MessageCircle, Minus, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ModalShell } from "@/components/ui/modal-shell";
import { useToast } from "@/hooks/use-toast";
import { extractApiErrorMessage } from "@/lib/api-error";
import { diaMes, fmtPontos, fmtPts, type LedgerLine } from "@/lib/loyalty";
import { cliqueNoLinkDoWhatsApp } from "@/lib/abreWhatsApp";

interface Vencendo {
  customerId: string;
  firstName: string | null;
  whatsappNumber: string;
  points: number;
  firstExpiry: string;
}

interface Resumo {
  mes: string;
  dados: number;
  trocados: number;
  vencidos: number;
  clientesComSaldo: number;
}

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const mesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const somaMes = (m: string, n: number) => {
  const [y, mm] = m.split("-").map(Number);
  const d = new Date(y!, mm! - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Página Clientes: resumo do mês do programa de pontos e quem tem pontos vencendo (botão abre o WhatsApp com a
// mensagem pronta — nada sai sozinho para o cliente, mesma regra do Parabenizar do aniversário).
export function PontosDaLoja({ storeName, storeSlug }: { storeName: string; storeSlug: string }) {
  const [mes, setMes] = useState(mesAtual);
  const { data: resumo } = useQuery<Resumo>({
    queryKey: ["loyalty-summary", mes],
    queryFn: () => customFetch(`/api/loyalty/summary?month=${mes}`),
  });
  const { data: vencendo = [] } = useQuery<Vencendo[]>({
    queryKey: ["loyalty-expiring", 7],
    queryFn: () => customFetch(`/api/loyalty/expiring?days=7`),
  });
  const [y, m] = mes.split("-").map(Number);
  const link = `${window.location.origin}/loja/${storeSlug}`;

  return (
    <Card className="p-4 space-y-4" data-testid="clientes-pontos">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold text-sm">
          <Gift className="w-4 h-4 text-primary" /> Programa de pontos
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <button type="button" className="p-1 rounded hover:bg-muted" aria-label="Mês anterior" onClick={() => setMes((v) => somaMes(v, -1))}>
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="min-w-[7rem] text-center capitalize">
            {MESES[(m ?? 1) - 1]} {y}
          </span>
          <button
            type="button"
            className="p-1 rounded hover:bg-muted disabled:opacity-30"
            aria-label="Próximo mês"
            disabled={mes >= mesAtual()}
            onClick={() => setMes((v) => somaMes(v, 1))}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[
          ["Dados", resumo?.dados],
          ["Trocados", resumo?.trocados],
          ["Vencidos", resumo?.vencidos],
        ].map(([rotulo, valor]) => (
          <div key={String(rotulo)} className="bg-muted/50 rounded-lg p-3 text-center">
            <p className="text-xl font-bold tabular-nums">{valor == null ? "—" : Number(valor).toLocaleString("pt-BR")}</p>
            <p className="text-xs text-muted-foreground">{rotulo}</p>
          </div>
        ))}
      </div>
      {resumo && <p className="text-xs text-muted-foreground">{resumo.clientesComSaldo} clientes com pontos para usar.</p>}

      {vencendo.length > 0 && (
        <div className="space-y-2 border-t pt-3">
          <p className="text-sm font-medium">Vencendo nos próximos 7 dias</p>
          {vencendo.map((v) => {
            const nome = (v.firstName || "Cliente").split(" ")[0];
            const digits = v.whatsappNumber.replace(/\D/g, "");
            const texto = `Oi ${nome}! Você tem ${fmtPontos(v.points)} na ${storeName} que vencem em ${diaMes(v.firstExpiry)}. Que tal usar no próximo pedido? ${link}`;
            return (
              <div key={v.customerId} className="flex items-center gap-2 text-sm">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 shrink-0">{diaMes(v.firstExpiry)}</span>
                <span className="truncate font-medium">{v.firstName || "Cliente"}</span>
                <span className="text-xs text-muted-foreground shrink-0">{fmtPts(v.points)}</span>
                {digits && !v.whatsappNumber.startsWith("__avulso__") && (
                  <a
                    href={`https://api.whatsapp.com/send?phone=${digits}&text=${encodeURIComponent(texto)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={cliqueNoLinkDoWhatsApp}
                    className="ml-auto shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-lg px-2 py-1 hover:bg-green-100"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Avisar
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

interface EstadoDoCliente {
  enabled: boolean;
  balance: number;
  expiring: Array<{ points: number; date: string }>;
  lines: LedgerLine[];
  won: Array<{ name: string; at: string; points: number }>;
  earnWays: Array<{ id: string; name: string; points: number }>;
}

// Detalhe do cliente (página Clientes): saldo, extrato e os botões Dar/Tirar pontos.
export function PontosDoCliente({ customerId, avulso }: { customerId: string; avulso: boolean }) {
  const { data, isLoading } = useQuery<EstadoDoCliente>({
    queryKey: ["loyalty-customer", customerId],
    queryFn: () => customFetch(`/api/loyalty/customers/${customerId}`),
    enabled: !avulso,
  });
  const [ajuste, setAjuste] = useState<"dar" | "tirar" | null>(null);

  if (avulso || isLoading || !data) return null;
  if (!data.enabled && data.lines.length === 0) return null;

  return (
    <Card className="p-6 space-y-4" data-testid="cliente-pontos">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Gift className="w-5 h-5 text-primary" /> Pontos
          </h3>
          <p className="text-2xl font-bold tabular-nums mt-1" data-testid="cliente-pontos-saldo">
            {fmtPontos(data.balance)}
          </p>
          {data.expiring.length > 0 && (
            <p className="text-xs text-amber-800 mt-0.5">
              {fmtPts(data.expiring[0]!.points)} vencem em {diaMes(data.expiring[0]!.date)}
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 w-full sm:w-auto">
          <Button size="sm" onClick={() => setAjuste("dar")} disabled={!data.enabled} data-testid="cliente-dar-pontos">
            <Plus className="w-4 h-4 mr-1" /> Dar pontos
          </Button>
          <Button size="sm" variant="outline" onClick={() => setAjuste("tirar")} disabled={data.balance <= 0} data-testid="cliente-tirar-pontos">
            <Minus className="w-4 h-4 mr-1" /> Tirar pontos
          </Button>
        </div>
      </div>

      {data.lines.length > 0 && (
        <ul className="divide-y divide-border/60">
          {data.lines.slice(0, 30).map((l) => (
            <li key={l.id} className="grid grid-cols-[3.5rem_1fr_auto] items-baseline gap-2 py-2 text-sm">
              <span className={`font-bold tabular-nums ${l.points > 0 ? "text-green-700" : "text-red-700"}`}>
                {l.points > 0 ? `+${l.points}` : `−${Math.abs(l.points)}`}
              </span>
              <span className="min-w-0 truncate">{l.description}</span>
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {l.open && l.expiresAt ? `vence ${diaMes(l.expiresAt)}` : diaMes(l.at)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {ajuste && (
        <AjusteDialog
          customerId={customerId}
          modo={ajuste}
          earnWays={data.earnWays}
          saldo={data.balance}
          onClose={() => setAjuste(null)}
        />
      )}
    </Card>
  );
}

function AjusteDialog({
  customerId,
  modo,
  earnWays,
  saldo,
  onClose,
}: {
  customerId: string;
  modo: "dar" | "tirar";
  earnWays: Array<{ id: string; name: string; points: number }>;
  saldo: number;
  onClose: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [pontos, setPontos] = useState("");
  const [salvando, setSalvando] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const n = Math.trunc(Number(pontos) || 0);
  const valido = motivo.trim().length > 0 && n > 0 && (modo === "dar" || n <= saldo);

  const salvar = async () => {
    if (!valido) return;
    setSalvando(true);
    try {
      await customFetch(`/api/loyalty/customers/${customerId}/adjust`, {
        method: "POST",
        body: JSON.stringify({ points: modo === "dar" ? n : -n, description: motivo.trim() }),
      });
      queryClient.invalidateQueries({ queryKey: ["loyalty-customer", customerId] });
      queryClient.invalidateQueries({ queryKey: ["loyalty-summary"] });
      toast({ title: modo === "dar" ? `${fmtPontos(n)} dados` : `${fmtPontos(n)} tirados` });
      onClose();
    } catch (err) {
      toast({ title: extractApiErrorMessage(err, "Não foi possível salvar."), variant: "destructive" });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <ModalShell
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={modo === "dar" ? "Dar pontos" : "Tirar pontos"}
      size="sm"
      footer={
        <Button className="w-full" disabled={!valido || salvando} onClick={salvar} data-testid="cliente-ajuste-salvar">
          {salvando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {modo === "dar" ? `Dar ${n > 0 ? fmtPontos(n) : "pontos"}` : `Tirar ${n > 0 ? fmtPontos(n) : "pontos"}`}
        </Button>
      }
    >
      <div className="space-y-4">
        {modo === "dar" && earnWays.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {earnWays.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => {
                  setMotivo(w.name);
                  setPontos(String(w.points));
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                  motivo === w.name ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"
                }`}
              >
                {w.name} · +{w.points}
              </button>
            ))}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="ajuste-motivo">Motivo</Label>
          <Input
            id="ajuste-motivo"
            value={motivo}
            maxLength={80}
            placeholder={modo === "dar" ? "Ex.: Postou no Instagram" : "Ex.: Ponto lançado errado"}
            onChange={(e) => setMotivo(e.target.value)}
            data-testid="cliente-ajuste-motivo"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ajuste-pontos">Pontos</Label>
          <Input
            id="ajuste-pontos"
            type="number"
            min={1}
            value={pontos}
            onChange={(e) => setPontos(e.target.value.replace(/\D/g, ""))}
            data-testid="cliente-ajuste-pontos"
          />
          {modo === "tirar" && <p className="text-xs text-muted-foreground">Saldo: {fmtPontos(saldo)}</p>}
        </div>
      </div>
    </ModalShell>
  );
}
